// Builds the plain-text version of a GeoBull / GeoBear report
// shown in the copy/paste box and used for clipboard + download.

export function buildReportText({ results, company, audit = null }) {
  if (!results || !results.parsed) return '';
  const parsed = results.parsed;

  let text = `GEOBULL / GEOBEAR ANALYSIS\n`;
  text += `X: @geobullbear | info@fruchet.com | www.geobullbear.com\n\n`;
  text += `Company: ${company}\n`;
  text += `Ticker: ${parsed.ticker}\n`;
  text += `Issue: ${parsed.issue}\n`;
  text += `Date: ${results.date}\n\n`;

  text += `EXECUTIVE SUMMARY\n${'='.repeat(60)}\n`;
  if (parsed.executive_summary && parsed.executive_summary.length > 0) {
    text += `Issue: ${parsed.executive_summary[0]}\n\n`;
    if (parsed.executive_summary.length > 1) {
      text += `Company: ${parsed.executive_summary[1]}\n\n`;
    }
  }

  text += `THE ISSUE\n${'='.repeat(60)}\n`;
  text += `${parsed.the_issue}\n\n`;

  text += `THE COMPANY\n${'='.repeat(60)}\n`;
  text += `${parsed.the_company}\n\n`;

  if (parsed.strategy_context) {
    text += `GEOPOLITICAL CONTEXT\n${'='.repeat(60)}\n`;
    if (parsed.strategy_context.rule_architecture) {
      text += `Rule Architecture:\n${parsed.strategy_context.rule_architecture}\n\n`;
    }
    if (parsed.strategy_context.leverage_map) {
      text += `Leverage Map:\n${parsed.strategy_context.leverage_map}\n\n`;
    }
  }

  text += `GEOBULLS\n${'='.repeat(60)}\n`;
  (parsed.bulls || []).forEach((bull) => {
    text += `${bull.number}. ${bull.label} | ${bull.title}\n`;
    text += `   ${bull.body}\n\n`;
  });

  text += `GEOBEARS\n${'='.repeat(60)}\n`;
  (parsed.bears || []).forEach((bear) => {
    text += `${bear.number}. ${bear.label} | ${bear.title}\n`;
    text += `   ${bear.body}\n\n`;
  });

  text += `WATCH\n${'='.repeat(60)}\n`;
  (parsed.watch || []).forEach((item) => {
    text += `- ${item.trigger} | ${item.detail}\n`;
  });

  // Credibility audit (if available)
  if (audit) {
    text += `\n\nCREDIBILITY AUDIT (head-to-head)\n${'='.repeat(60)}\n`;
    const winnerWins =
      audit.verdict === 'BULLS' ? audit.bulls_wins : audit.bears_wins;
    text += `Verdict: ${audit.verdict} more credible — won ${winnerWins} of 25 head-to-head matchups.\n`;
    text += `Score: Bulls ${audit.bulls_wins} / Bears ${audit.bears_wins} (out of 25)\n`;
    if (typeof audit.avg_decisiveness === 'number') {
      const qual =
        audit.avg_decisiveness >= 2.3
          ? 'mostly decisive'
          : audit.avg_decisiveness >= 1.7
          ? 'mostly clear'
          : 'mostly close';
      text += `Avg decisiveness: ${audit.avg_decisiveness.toFixed(2)} / 3.00 (${qual})\n`;
    }
    if (audit.is_directional) {
      text += `Note: directional verdict — wide count margin but most matchups were close calls.\n`;
    }
    if (audit.dimension_breakdown) {
      const d = audit.dimension_breakdown;
      text += `Deciding dimensions: source_primacy=${d.source_primacy ?? 0} · recency=${d.recency ?? 0} · magnitude_fit=${d.magnitude_fit ?? 0} · counterfactual=${d.counterfactual ?? 0}\n`;
    }
    if (audit.verdict_summary) text += `Summary: ${audit.verdict_summary}\n`;

    const fmtRec = (r) => {
      const rec = `${r.wins}W-${r.losses}L`.padEnd(6);
      return `#${r.number} [${rec}] ${r.label}\n   "${r.top_quote}"`;
    };

    text += `\n-- Bull arguments --\n`;
    (audit.bulls || []).forEach((r) => {
      text += `${fmtRec(r)}\n`;
    });
    text += `\n-- Bear arguments --\n`;
    (audit.bears || []).forEach((r) => {
      text += `${fmtRec(r)}\n`;
    });
    text += `\nMethodology: each bull argument is matched 1-on-1 against each bear argument (5x5=25 matchups). Each decided on the most decisive of: source primacy, recency, magnitude–evidence fit, or counterfactual specificity. Decisiveness rated 1 (close) to 3 (blowout). Each dimension must decide ≥4 matchups.\n`;
  }

  text += `\n\nDisclaimer: this report was produced using artificial intelligence and may contain errors.`;
  text += `\n\n© Fruchet Consulting LLC`;
  return text;
}

export function buildReportFilename({ results, company }) {
  const ticker = (results?.parsed?.ticker || company || 'report')
    .toString()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .toUpperCase();
  const date = (results?.date || new Date().toISOString().slice(0, 10))
    .toString()
    .replace(/[^0-9-]/g, '')
    .slice(0, 10);
  return `GeoBull-GeoBear_${ticker}_${date}.txt`;
}
